import i18n from '@/i18n';
import { format } from 'date-fns';
import { fundingAuthAPI, getIpeWorkspaceMode, taskAPI } from '@/utils/api';
import { allocateTaskIdentifiersLocal } from '@/pages/ipe/workbench/utils/taskIdGenerator';
import { createIpeWorkflowTask } from '@/pages/ipe/workbench/utils/ipeWorkflowTaskRecord';
import { buildPodBatchGoodsForms } from '@/pages/ipe/workbench/utils/podTemplateMeta';
import { runGoodsTaskBackgroundProcessing } from '@/pages/ipe/workbench/utils/goodsTaskProcessor';
import {
  buildGoodsLineageFields,
  buildPersistedGoodsPrintUrlMap,
  inferCropTaskIdFromImageIds,
  persistGoodsPrintUrlsToSession,
} from '@/pages/ipe/workbench/utils/goodsPrintUrlResolve';
import { buildTaskImageAssetsPayload } from '@/pages/ipe/workbench/utils/imageAssetModel';
import { fetchImageTemplateCatalogEntries } from '@/pages/ipe/workbench/utils/templateListUtils';
import {
  annotateImageTemplateScopes,
  filterImageTemplatesByUserTeamAccess,
} from '@/pages/ipe/workbench/utils/imageTemplateAccess';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import { trackPendingTask } from '@/utils/workbenchHistoryBadge';
import { buildHistoryPanelRestoreSnapshot } from '@/pages/ipe/workbench/utils/workbenchHistoryPanelRestore';
import { isGoodsTemplateServerWorkerEnabled } from '@/pages/ipe/workbench/utils/goodsTemplateServerWorkerFlag';

/**
 * 详情页联动「主图创作 / POD商品设计」：选模版后本页创建任务并后台合成。
 * POD 建议回工作台并打开历史面板（避免直进详情时错过进度推送看不到成品）。
 * @param {{ sources?: object[], templates?: object[], billingNavId?: string, fromCropTaskId?: string }} params
 */
export async function createGoodsTaskFromDetailSources({
  sources = [],
  templates = [],
  billingNavId = 'goods',
  fromCropTaskId = '',
}) {
  const workspaceMode = getIpeWorkspaceMode();
  const stampRefs = (sources || [])
    .map((s) => {
      const id = String(s?.id || '').trim();
      const url = String(s?.url || s?.previewUrl || '').trim();
      if (!id || !url) return null;
      return {
        role: 'print',
        assetId: s.assetId,
        id,
        name: s.name || id,
        previewUrl: String(s.previewUrl || url).trim(),
        apiUrl: url,
      };
    })
    .filter(Boolean);

  if (stampRefs.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }
  if (!templates.length) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectTemplateSetFirst'));
  }

  const isPod = billingNavId === 'podProductDesign';
  const sourceNav = isPod ? 'podProductDesign' : 'goods';
  const taskName = isPod
    ? i18n.t('workbench:ipeWorkbench.podTaskName')
    : i18n.t('workbench:ipeWorkbench.goodsTaskName');
  const taskTitlePrefix = taskName;

  const rawPrintUrlMap = {};
  stampRefs.forEach((ref) => {
    rawPrintUrlMap[String(ref.id)] = ref.apiUrl;
  });
  const lineageCropTaskId = String(fromCropTaskId || '').trim()
    || inferCropTaskIdFromImageIds(stampRefs.map((r) => r.id));
  const printUrlMap = buildPersistedGoodsPrintUrlMap(rawPrintUrlMap, lineageCropTaskId);
  persistGoodsPrintUrlsToSession(lineageCropTaskId, printUrlMap);

  const goodsForms = buildPodBatchGoodsForms(templates, stampRefs, {
    printUrlOverrides: printUrlMap,
  });
  if (!goodsForms.length) {
    throw new Error(i18n.t('workbench:ipeUtilError.templateSetGenerateFailed'));
  }

  const total = goodsForms.reduce((sum, form) => sum + (form.imageGroups?.length || 0), 0);
  const { taskId, subAccount } = allocateTaskIdentifiersLocal('goods');
  const now = format(new Date(), 'yyyy-MM-dd HH:mm');
  const previewUrl = stampRefs[0]?.previewUrl || stampRefs[0]?.apiUrl || '';
  const formsForTask = goodsForms.map((form) => ({
    ...form,
    compositeResults: [],
  }));
  const imageAssets = buildTaskImageAssetsPayload({
    stampRefs,
    printUrlByImageId: printUrlMap,
    goodsForms: formsForTask,
    sourceTaskId: lineageCropTaskId || taskId,
    sourceModule: sourceNav,
  });
  const useServerWorker = isGoodsTemplateServerWorkerEnabled();
  const result = {
    previewUrl,
    imageIds: stampRefs.map((r) => String(r.id)),
    goodsPrintUrlByImageId: printUrlMap,
    imageAssets,
    sourceNav,
    billingNavId: sourceNav,
    goodsForms: formsForTask,
    ...buildGoodsLineageFields(lineageCropTaskId),
    ...(useServerWorker ? { serverWorker: true, batchPhase: 'queued' } : {}),
  };

  const newTask = {
    id: taskId,
    subAccount,
    billingNavId: sourceNav,
    total: Math.max(1, total),
    successCount: 0,
    failedCount: 0,
    status: 'processing',
    createdAt: now,
    finishedAt: '—',
    imageTemplateIds: formsForTask.map((f) => f.templateId),
    imageIds: stampRefs.map((r) => String(r.id)),
    previewUrl,
    result,
    goodsForms: formsForTask,
  };

  await createIpeWorkflowTask({
    id: taskId,
    type: 'goods',
    name: taskName,
    title: `${taskTitlePrefix} ${subAccount}`,
    subAccount,
    billingNavId: sourceNav,
    imageIds: stampRefs.map((r) => String(r.id)),
    previewUrl,
    status: 'processing',
    total: Math.max(1, total),
    successCount: 0,
    failedCount: 0,
    result,
    goodsForms: formsForTask,
  });

  const historyScopeId = isPod ? 'podBatch' : 'goodsStudio';
  trackPendingTask(historyScopeId, taskId, workspaceMode);

  if (useServerWorker) {
    taskAPI.submit(taskId).catch((error) => {
      console.error('[详情联动套图任务] 服务端合成入队失败', error);
    });
  } else {
    // 后台合成，不阻塞跳转
    runGoodsTaskBackgroundProcessing({
      taskId,
      effectiveGoodsForms: formsForTask,
      goodsPrintUrlByImageId: printUrlMap,
      fromCropTaskId: lineageCropTaskId || undefined,
      taskResultBase: result,
      billingNav: sourceNav,
    }).catch((error) => {
      console.error('[详情联动套图任务] 后台合成失败', error);
    });
  }

  return {
    ...newTask,
    restoreHistoryPanel: buildHistoryPanelRestoreSnapshot(sourceNav, {
      highlightTaskId: taskId,
    }),
  };
}

/**
 * 加载套图模版列表（详情快捷 → POD商品设计 / 主图选模板）。
 * 按用户个人 + 团队权限过滤，并打上 official/personal/team scope。
 * @returns {Promise<{ templates: object[], canAccessTeam: boolean }>}
 */
export async function loadImageTemplatesForDetailBridge() {
  const catalog = await fetchImageTemplateCatalogEntries();
  let me = null;
  let canAccessTeam = false;
  let teamId = null;
  let teamNo = '';

  try {
    me = await fundingAuthAPI.getMe();
  } catch {
    me = null;
  }
  const user = me?.user || me?.data || me || null;
  const authUserId = user?.id ?? user?.userId ?? null;
  const userNo = user?.userNo ? String(user.userNo).trim() : '';

  try {
    const center = await fundingAuthAPI.getAccountCenter();
    canAccessTeam = isTeamSpaceEntryAvailable(center);
  } catch {
    canAccessTeam = false;
  }

  if (canAccessTeam) {
    try {
      const overview = await fundingAuthAPI.getTeamOverview();
      const team = overview?.team && typeof overview.team === 'object' ? overview.team : overview;
      teamId = team?.id ?? team?.teamId ?? null;
      teamNo = team?.teamNo ?? team?.team_no ?? '';
    } catch {
      teamId = null;
      teamNo = '';
    }
  }

  const filtered = filterImageTemplatesByUserTeamAccess(catalog, {
    authUserId,
    userNo,
    canAccessTeam,
    teamId,
    teamNo,
  });

  return {
    templates: annotateImageTemplateScopes(filtered),
    canAccessTeam,
  };
}
